import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

interface RewardAccount {
  accountId: string;
  accountName: string;
  pointsBalance: number;
  totalPointsEarned: number;
  totalPointsRedeemed: number;
  tierLevel: 'bronze' | 'silver' | 'gold' | 'platinum';
  memberSince: Date;
  lastTransactionDate: Date;
}

interface RedemptionOption {
  id: string;
  name: string;
  pointsCost: number;
  description: string;
  category: string;
}

interface PointsTransaction {
  id: string;
  accountId: string;
  type: 'earn' | 'redeem' | 'adjust' | 'expire';
  points: number;
  reason: string;
  createdAt: Date;
}

export default function RewardsManager() {
  const [accountId, setAccountId] = useState('');
  const [rewardAccount, setRewardAccount] = useState<RewardAccount | null>(null);
  const [catalog, setCatalog] = useState<RedemptionOption[]>([]);
  const [history, setHistory] = useState<PointsTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedRedemption, setSelectedRedemption] = useState<RedemptionOption | null>(null);

  const fetchRewardData = async () => {
    if (!accountId) return;
    try {
      setLoading(true);
      const [accountRes, catalogRes, historyRes] = await Promise.all([
        apiClient.getRewardAccount(accountId),
        apiClient.getRedemptionCatalog(),
        apiClient.getRewardsHistory(accountId),
      ]);

      setRewardAccount(accountRes.data);
      setCatalog(catalogRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to fetch rewards data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (option: RedemptionOption) => {
    if (!rewardAccount || rewardAccount.pointsBalance < option.pointsCost) {
      alert('Insufficient points');
      return;
    }

    try {
      await apiClient.redeemPoints(accountId, option.pointsCost, `Redeemed: ${option.name}`);
      setSelectedRedemption(null);
      fetchRewardData();
    } catch (error) {
      console.error('Failed to redeem points:', error);
      alert('Redemption failed');
    }
  };

  const getTierColor = (tier: string) => {
    const colors: Record<string, string> = {
      bronze: '#CD7F32',
      silver: '#C0C0C0',
      gold: '#FFD700',
      platinum: '#E5E4E2',
    };
    return colors[tier] || '#6c757d';
  };

  const getTierIcon = (tier: string) => {
    const icons: Record<string, string> = {
      bronze: '🥉',
      silver: '🥈',
      gold: '🥇',
      platinum: '👑',
    };
    return icons[tier] || '⭐';
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      discount: '💰',
      product: '🛍️',
      service: '🎁',
      experience: '🎉',
    };
    return icons[category] || '📦';
  };

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px', fontFamily: 'system-ui' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2>🏆 Rewards & Loyalty Program</h2>
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <input
            type="text"
            placeholder="Enter Account ID"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
          <button
            onClick={fetchRewardData}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Load
          </button>
        </div>
      </div>

      {loading ? (
        <p style={{ textAlign: 'center' }}>Loading...</p>
      ) : rewardAccount ? (
        <div>
          {/* Tier & Points Overview */}
          <div
            style={{
              backgroundColor: getTierColor(rewardAccount.tierLevel),
              color: '#fff',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '40px', marginBottom: '10px' }}>
              {getTierIcon(rewardAccount.tierLevel)}
            </div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', textTransform: 'capitalize' }}>
              {rewardAccount.tierLevel} Member
            </div>
            <div style={{ fontSize: '14px', marginTop: '5px' }}>
              {rewardAccount.accountName}
            </div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', marginTop: '15px' }}>
              {rewardAccount.pointsBalance.toLocaleString()} 🪙
            </div>
            <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.9 }}>
              Member since {new Date(rewardAccount.memberSince).toLocaleDateString('en-ZA')}
            </div>
          </div>

          {/* Stats Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
            <div style={{ padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '4px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Total Earned</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {rewardAccount.totalPointsEarned.toLocaleString()}
              </div>
            </div>
            <div style={{ padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '4px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Total Redeemed</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {rewardAccount.totalPointsRedeemed.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', borderBottom: '1px solid #ddd' }}>
            {['overview', 'catalog', 'history'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '10px 15px',
                  backgroundColor: activeTab === tab ? '#007bff' : 'transparent',
                  color: activeTab === tab ? '#fff' : '#666',
                  border: 'none',
                  borderBottomWidth: activeTab === tab ? '0' : '0',
                  cursor: 'pointer',
                  fontWeight: activeTab === tab ? 'bold' : 'normal',
                  textTransform: 'capitalize',
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              <h3>Quick Stats</h3>
              <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '4px' }}>
                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Points This Month:</span>
                  <span style={{ fontWeight: 'bold' }}>+{Math.floor(rewardAccount.totalPointsEarned * 0.1)}</span>
                </div>
                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Burn Rate (avg):</span>
                  <span style={{ fontWeight: 'bold' }}>{rewardAccount.totalPointsRedeemed > 0 ? Math.round(rewardAccount.totalPointsEarned / rewardAccount.totalPointsRedeemed) : '0'}:1</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Last Redemption:</span>
                  <span style={{ fontWeight: 'bold' }}>
                    {new Date(rewardAccount.lastTransactionDate).toLocaleDateString('en-ZA')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Catalog Tab */}
          {activeTab === 'catalog' && (
            <div>
              <h3>Redemption Catalog</h3>
              {catalog.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#666' }}>No redemption options available</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                  {catalog.map((option) => (
                    <div
                      key={option.id}
                      style={{
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                            {getCategoryIcon(option.category)} {option.name}
                          </div>
                          {option.description && (
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                              {option.description}
                            </div>
                          )}
                          <div style={{ fontSize: '12px', marginTop: '4px', textTransform: 'capitalize', color: '#999' }}>
                            {option.category}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#FFD700', marginBottom: '8px' }}>
                            {option.pointsCost}
                          </div>
                          <button
                            onClick={() => {
                              if (rewardAccount.pointsBalance >= option.pointsCost) {
                                setSelectedRedemption(option);
                              } else {
                                alert('Insufficient points');
                              }
                            }}
                            disabled={rewardAccount.pointsBalance < option.pointsCost}
                            style={{
                              padding: '6px 12px',
                              backgroundColor:
                                rewardAccount.pointsBalance >= option.pointsCost ? '#28a745' : '#ccc',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor:
                                rewardAccount.pointsBalance >= option.pointsCost
                                  ? 'pointer'
                                  : 'not-allowed',
                              fontSize: '12px',
                            }}
                          >
                            Redeem
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div>
              <h3>Transaction History</h3>
              {history.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#666' }}>No transactions yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {history.slice(0, 20).map((tx) => (
                    <div
                      key={tx.id}
                      style={{
                        padding: '10px',
                        border: '1px solid #eee',
                        borderRadius: '4px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 'bold', textTransform: 'capitalize' }}>
                          {tx.type === 'earn' && '✅'} {tx.type === 'redeem' && '🎁'} {tx.reason}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>
                          {new Date(tx.createdAt).toLocaleDateString('en-ZA')}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: tx.type === 'earn' ? '#28a745' : '#dc3545',
                        }}
                      >
                        {tx.type === 'earn' ? '+' : ''}{tx.points}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Redemption Confirmation */}
          {selectedRedemption && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
              }}
            >
              <div
                style={{
                  backgroundColor: '#fff',
                  padding: '30px',
                  borderRadius: '8px',
                  maxWidth: '400px',
                  textAlign: 'center',
                }}
              >
                <h3>Confirm Redemption</h3>
                <div style={{ margin: '20px 0' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                    {selectedRedemption.name}
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#FFD700', marginBottom: '10px' }}>
                    {selectedRedemption.pointsCost}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    Available: {rewardAccount.pointsBalance} points
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                  <button
                    onClick={() => handleRedeem(selectedRedemption)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#28a745',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                    }}
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => setSelectedRedemption(null)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#ccc',
                      color: '#000',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <p style={{ textAlign: 'center', color: '#666' }}>Enter an account ID to view rewards</p>
      )}
    </div>
  );
}
